import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listSubscriptions, createSubscription, deleteSubscription } from "@/lib/notifications.functions";
import { listSlackWorkspaces, listSlackChannels } from "@/lib/integrations/slack.functions";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/notifications")({ component: NotificationsAdmin });

const EVENTS = [
  "ship_event",
  "low_stock",
  "cycle_count_variance",
  "invoice_issued",
  "dunning_step",
  "portal_order_submitted",
  "portal_rma_submitted",
  "edi_failed",
  "carrier_cert_completed",
];

function NotificationsAdmin() {
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const fetchSubs = useServerFn(listSubscriptions);
  const createFn = useServerFn(createSubscription);
  const delFn = useServerFn(deleteSubscription);
  const fetchSlackWs = useServerFn(listSlackWorkspaces);
  const fetchSlackChannels = useServerFn(listSlackChannels);

  const { data, isLoading } = useQuery({
    queryKey: ["notif-subs"],
    queryFn: () => fetchSubs({}),
  });
  const { data: slackData } = useQuery({
    queryKey: ["slack-workspaces"],
    queryFn: () => fetchSlackWs({}),
  });
  const workspaces = (slackData?.workspaces ?? []) as Array<{ id: string; team_name: string | null; team_id: string }>;

  const [eventType, setEventType] = useState(EVENTS[0]);
  const [channel, setChannel] = useState<"email" | "slack" | "slack_app" | "webhook">("email");
  const [target, setTarget] = useState("");
  const [secret, setSecret] = useState("");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [channels, setChannels] = useState<Array<{ id: string; name: string; is_private: boolean }>>([]);

  async function loadChannels(ws: string) {
    setWorkspaceId(ws);
    setTarget("");
    try {
      const r = await fetchSlackChannels({ data: { workspace_id: ws } });
      setChannels(r.channels);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function add() {
    if (!tenantId) return toast.error("Select a tenant");
    if (!target) return toast.error("Target is required");
    if (channel === "slack_app" && !workspaceId) return toast.error("Pick a Slack workspace");
    try {
      await createFn({
        data: {
          tenantId,
          eventType,
          channel,
          target,
          hmacSecret: secret || undefined,
          slackWorkspaceId: channel === "slack_app" ? workspaceId : undefined,
        },
      });
      toast.success("Subscription added");
      setTarget(""); setSecret(""); setWorkspaceId(""); setChannels([]);
      qc.invalidateQueries({ queryKey: ["notif-subs"] });
    } catch (e) { toast.error((e as Error).message); }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-foreground">Email, Slack, and signed webhook subscriptions per event.</p>
        </div>
        <Link to="/app/admin/slack" className="text-sm underline text-muted-foreground hover:text-foreground">
          Manage Slack workspaces →
        </Link>
      </div>

      <Card>
        <CardHeader><CardTitle>New Subscription</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>Event</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EVENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Channel</Label>
            <Select value={channel} onValueChange={(v) => setChannel(v as typeof channel)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="slack_app" disabled={workspaces.length === 0}>
                  Slack app{workspaces.length === 0 ? " (connect first)" : ""}
                </SelectItem>
                <SelectItem value="slack">Slack webhook (legacy)</SelectItem>
                <SelectItem value="webhook">HTTPS webhook</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {channel === "slack_app" ? (
            <>
              <div>
                <Label>Workspace</Label>
                <Select value={workspaceId} onValueChange={loadChannels}>
                  <SelectTrigger><SelectValue placeholder="Pick workspace" /></SelectTrigger>
                  <SelectContent>
                    {workspaces.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.team_name ?? w.team_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={target} onValueChange={setTarget} disabled={channels.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={channels.length === 0 ? "Pick workspace first" : "Pick channel"} />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.is_private ? "🔒 " : "# "}{c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div className="md:col-span-2">
              <Label>
                Target {channel === "email" ? "(email)" : "(URL)"}
              </Label>
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder={channel === "email" ? "ops@acme.com" : "https://..."}
              />
            </div>
          )}
          {channel === "webhook" && (
            <div>
              <Label>HMAC secret</Label>
              <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="optional" />
            </div>
          )}
          <Button onClick={add}><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active Subscriptions</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <p>Loading…</p> : (
            <div className="overflow-x-auto"><Table>
              <TableHeader>
                <TableRow><TableHead>Event</TableHead><TableHead>Channel</TableHead><TableHead>Target</TableHead><TableHead className="w-16"></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rows ?? []).map((r: { id: string; event_type: string; channel: string; target: string }) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.event_type}</TableCell>
                    <TableCell>{r.channel}</TableCell>
                    <TableCell className="truncate max-w-md">{r.target}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={async () => { await delFn({ data: { id: r.id } }); qc.invalidateQueries({ queryKey: ["notif-subs"] }); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}